import {
  distanceEquals,
  distanceGreaterThanOrEqual,
  distanceLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Point2D,
  Polygon2D,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export class SelectionWindow {
  pointA: Point2D;
  pointB: Point2D;

  get minX() {
    return Math.min(this.pointA.x, this.pointB.x);
  }

  get maxX() {
    return Math.max(this.pointA.x, this.pointB.x);
  }

  get minY() {
    return Math.min(this.pointA.y, this.pointB.y);
  }

  get maxY() {
    return Math.max(this.pointA.y, this.pointB.y);
  }

  /**
   * Only contains edges that are greater than distance tolerance.
   */
  get edges() {
    const xEdges = !distanceEquals(this.pointA.x, this.pointB.x)
      ? [
          new LineSegment2D(this.pointA, new Point2D(this.pointB.x, this.pointA.y)),
          new LineSegment2D(this.pointB, new Point2D(this.pointA.x, this.pointB.y)),
        ]
      : [];
    const yEdges = !distanceEquals(this.pointA.y, this.pointB.y)
      ? [
          new LineSegment2D(this.pointA, new Point2D(this.pointA.x, this.pointB.y)),
          new LineSegment2D(this.pointB, new Point2D(this.pointB.x, this.pointA.y)),
        ]
      : [];
    return [...xEdges, ...yEdges];
  }

  constructor(pointA: Point2D, pointB: Point2D) {
    this.pointA = pointA;
    this.pointB = pointB;
  }

  containsPoint(point: Point2D) {
    return (
      distanceGreaterThanOrEqual(point.x, this.minX) &&
      distanceLessThanOrEqual(point.x, this.maxX) &&
      distanceGreaterThanOrEqual(point.y, this.minY) &&
      distanceLessThanOrEqual(point.y, this.maxY)
    );
  }

  overlaps(shape: Line2D | LineSegment2D | Polygon2D) {
    if (shape instanceof Polygon2D) {
      return (
        this.containsPoint(shape.centroid) ||
        shape.containsPoint(this.pointA) ||
        this.edges.some((selectionWindowEdge) =>
          shape.edges.some((polygonEdge) => selectionWindowEdge.intersectWithSegment(polygonEdge)),
        )
      );
    }

    if (shape instanceof Line2D) {
      return this.edges.some((edge) => this.containsPoint(shape.basePoint) || edge.intersectWithLine(shape));
    }

    if (shape instanceof LineSegment2D) {
      return this.edges.some((edge) => this.containsPoint(shape.basePoint) || edge.intersectWithSegment(shape));
    }

    assertExhaustiveSwitch(shape);
  }
}
